// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'

import { updateClientProfile } from './clientProfile'

/**
 * Edit a client's name / phone from the Clients page.
 *
 * A client record is a `users` row, not a resource this portal has an Edit view
 * for — `src/owners/index.tsx` exports `list` only. Rather than introduce a
 * full RA Edit view over `users` (which would need a dataProvider update path
 * that `Users.access.update` deliberately denies to vets), this writes the two
 * whitelisted fields through `PATCH /api/users/client-profile`.
 *
 * Email is intentionally NOT editable here: changing a client's login address
 * from the clinic side is an account-takeover primitive, and the backend
 * endpoint refuses the field outright.
 */
interface Props {
  open: boolean
  ownerId: number | string
  initialName: string
  initialPhone: string
  onClose: () => void
  onSaved: () => void
}

const EditClientDialog: React.FC<Props> = ({
  open,
  ownerId,
  initialName,
  initialPhone,
  onClose,
  onSaved,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [name, setName] = useState(initialName)
  const [phone, setPhone] = useState(initialPhone)
  const [saving, setSaving] = useState(false)

  const trimmedName = name.trim()
  const nameInvalid = trimmedName.length < 2

  const handleSave = async () => {
    if (nameInvalid) return
    setSaving(true)
    const result = await updateClientProfile({
      ownerId,
      name: trimmedName,
      phone: phone.trim(),
    })
    setSaving(false)

    if (result.ok) {
      notify('pos.client_profile.saved', { type: 'success' })
      onSaved()
      onClose()
      return
    }
    if (result.status === 403 || result.status === 404) {
      notify('pos.client_profile.not_allowed', { type: 'warning' })
      return
    }
    notify('pos.client_profile.save_error', { type: 'error' })
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{translate('pos.client_profile.dialog_title')}</DialogTitle>
      <DialogContent>
        <TextField
          label={translate('pos.pet_edit.owner_name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameInvalid}
          helperText={nameInvalid ? translate('pos.client_profile.name_too_short') : undefined}
          fullWidth
          margin="dense"
          disabled={saving}
          inputProps={{ 'data-testid': 'client-dialog-name', maxLength: 200 }}
        />
        <TextField
          label={translate('pos.owners.phone')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          margin="dense"
          disabled={saving}
          inputProps={{ 'data-testid': 'client-dialog-phone', maxLength: 40 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={nameInvalid || saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          {translate('pos.client_profile.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditClientDialog
